// Bildschirm "Wiedervorlagen" -- was ich mir vorgenommen habe (ST-ACT-001,
// zweiter Schnitt; ADR-0052, Entscheidung 6).
//
// ER BEANTWORTET EINE FRAGE: was steht heute an? Deshalb die Faelligsten
// zuerst, ueberfaellige oben, und je Zeile der Kontakt, um den es geht -- "am
// 22.09." ohne "bei wem" waere keine Antwort. Der Name kommt aus der
// Feldgruppe `names`, die Michael Brozat am 17.09.2026 dem Zweck zugeordnet
// hat (ADR-0052 Ziffer 7); ohne sie waeren es zwanzig Einzelabrufe je Seite.
//
// GENAU EINE HAUPTAKTION gibt es hier NICHT -- und das ist die Aussage: dieser
// Bildschirm ist eine Liste zum Abarbeiten, keine Maske zum Ausfuellen. Was er
// anbietet, ist je Zeile "Erledigt" und "Nicht mehr nötig" (gleichrangig, weil
// beide dasselbe Gewicht haben) und den Weg zum Kontakt als Link. Eine neue
// Wiedervorlage entsteht NICHT hier, sondern an der Notiz, zu der sie gehoert
// (5.2.2: im Arbeitsfluss, nicht in einem Verwaltungsbildschirm).
//
// KEINE ERINNERUNG, und der Bildschirm verspricht auch keine (Entscheidung 5;
// ADR-0002: keine Attrappe). Diese Liste IST der Weckruf.
//
// GENAU EINE STATUSREGION mit aria-live; der Fokus wandert nach jeder Aktion
// dorthin (Muster A15, NFR-ACC-001).
//
// GESTALT NACH v4: h1 mit Zaehlsatz im Seitenkopf, eine Karte, darin die
// Gruppen Ueberfaellig / Heute / Spaeter mit je einer benannten Liste. Der
// Zaehlsatz im Kopf ist nur fuers Auge; Hilfsmittel hoeren die Zahl aus der
// Statusregion, die sie nach jeder Aktion neu ansagt.

import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import {
  Body1,
  Body1Strong,
  Button,
  Caption1,
  Card,
  Field,
  Input,
  makeStyles,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Select,
  Title3,
  tokens,
} from '@fluentui/react-components';

import type { PlainMessage } from '../access.ts';
import { partyHref } from '../parties.ts';
import {
  cancelFollowUp,
  changeFollowUpDue,
  completeFollowUp,
  dueLabel,
  dueOnFieldProblem,
  FAELLIGKEITEN,
  faelligkeitFuer,
  type FaelligkeitId,
  FAELLIGKEITS_GRUPPEN,
  fetchOwnTasks,
  taskButtonName,
  groupByDue,
  openSummary,
  reopenFollowUp,
  taskSummary,
  todayInBerlin,
  type TaskView,
} from '../tasks.ts';
import { AppLink } from '../shell/router.tsx';
import { MehrKnopf } from '../shell/mehr-knopf.tsx';
import { Skelett, SKELETT_WIEDERVORLAGEN } from '../shell/skelett.tsx';
import { useSeitenueberschrift, useUeberschriftImInhalt } from '../shell/layout.tsx';
import { Vorgang, Zustand } from '../shell/zustand.tsx';
import { NebenKnopf } from '../shell/nebenknopf.tsx';

const VERBORGEN = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

const useStyles = makeStyles({
  kopf: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: '0.25rem 1rem',
    marginBottom: '1rem',
  },
  titel: { margin: 0, marginRight: 'auto', overflowWrap: 'anywhere' },
  zaehlsatz: { color: tokens.colorNeutralForeground2, fontVariantNumeric: 'tabular-nums' },
  karte: {
    // Die Zeilen laufen bis an den Kartenrand (v4 .panel); den Abstand tragen sie selbst.
    padding: 0,
    gap: 0,
    boxShadow: tokens.shadow4,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: 'hidden',
  },
  // Die Statusregion bleibt immer im Baum (Fokusziel); Abstand nur, wenn sie etwas zeigt.
  region: { ':focus': { outline: 'none' }, ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: '-2px' } },
  regionInhalt: { display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '1rem' },
  verborgen: VERBORGEN,
  gruppenkopf: {
    margin: 0,
    padding: '0.875rem 1rem 0.375rem',
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: tokens.colorNeutralForeground3,
  },
  // Die Farbe kommt zum Wort "Überfällig" dazu, sie ersetzt es nie (NFR-ACC-001, CM-UX-002).
  gruppenkopfUeberfaellig: { color: tokens.colorPaletteRedForeground1 },
  liste: { listStyle: 'none', margin: 0, padding: 0 },
  eintrag: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.5rem 0.875rem',
    padding: '0.625rem 1rem',
    minHeight: '44px',
    boxSizing: 'border-box',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    ':focus': { outline: 'none' },
    ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: '-2px' },
  },
  // 16rem Grundbreite: daneben passen die Knoepfe ab Tablet, darunter brechen sie um (390 px).
  text: { flex: '1 1 16rem', minWidth: 0, display: 'grid', gap: '0.125rem' },
  titelZeile: { overflowWrap: 'anywhere' },
  unterzeile: { color: tokens.colorNeutralForeground2, overflowWrap: 'anywhere' },
  knoepfe: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem' },
  // 28 px wie v4 .btn.small; Fluent small hat nur 24, genau die Untergrenze aus WCAG 2.5.8.
  knopf: { minHeight: '28px' },
  // Bis 400 px passen drei Knoepfe nicht neben den Titel: "Erledigt" bleibt, der Rest geht ins "…"
  // (DESIGN-P3 #561, Muster Verlaufseintrag). Je Breite ist genau eine Fassung im Baum sichtbar.
  breit: { display: 'contents', '@media (max-width: 400px)': { display: 'none' } },
  schmal: { display: 'none', '@media (max-width: 400px)': { display: 'inline-flex' } },
  // Das "…" so gross wie "Erledigt" daneben: am Handy sonst nur die WCAG-Untergrenze (DESIGN-P3 #601).
  mehr: { minWidth: '32px', minHeight: '32px' },
  aktionen: { display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.75rem' },
  // Das Verschieben klappt unter der Zeile auf, ueber die volle Breite (bricht bei 320 px um).
  verschieben: { flex: '1 1 100%', display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '0.5rem 0.75rem' },
  verschiebenFeld: { flex: '1 1 12rem', minWidth: 0 },
});

/** Die Optionen des Verschiebens: dieselben wie beim Setzen an der Notiz, ohne "keine". */
const VERSCHIEBEN_OPTIONEN = FAELLIGKEITEN.filter((option) => option.id !== 'keine');

/** Haken vor "Erledigt" (v4), nur Zierde: der Knopftext traegt die Aussage. */
function Haken(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
      <path d="m3.5 8.5 3 3 6-7" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const TITEL_ID = 'wiedervorlagen-titel';

export interface WiedervorlagenScreenProps {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
}

export const STATUS_REGION_ID = 'wiedervorlagen-status';

const zeilenId = (taskId: string): string => `wiedervorlage-${taskId}`;

export function WiedervorlagenScreen({ apiBaseUrl, accessToken, onReauthenticate }: WiedervorlagenScreenProps): ReactElement {
  useUeberschriftImInhalt();
  useSeitenueberschrift('Wiedervorlagen');
  const own = useStyles();
  const [eintraege, setEintraege] = useState<readonly TaskView[]>([]);
  const [status, setStatus] = useState<'laedt' | 'geladen' | 'abgelehnt'>('laedt');
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [ladefehler, setLadefehler] = useState<{ readonly message: PlainMessage; readonly cursor?: string } | undefined>(undefined);
  const [message, setMessage] = useState<PlainMessage | undefined>(undefined);
  const [bestaetigung, setBestaetigung] = useState<string | undefined>(undefined);
  // Der Rueckweg nach "Erledigt" (taskId und neue lockVersion aus dessen Antwort); er gilt nur,
  // solange die Statusregion SEINEN Satz zeigt, jede neue Ansage nimmt den Knopf mit.
  const [rueckgang, setRueckgang] = useState<
    { readonly satz: string; readonly taskId: string; readonly lockVersion: number; readonly titel: string; readonly name: string } | undefined
  >(undefined);
  const [busy, setBusy] = useState(false);
  const statusRef = useRef<HTMLDivElement | null>(null);
  const [fokusAnfordern, setFokusAnfordern] = useState(0);
  const laufRef = useRef(0);
  // Erst nach einem ausdruecklichen "Erneut versuchen" geht der Fokus auf den Titel.
  const [nachErneut, setNachErneut] = useState(false);
  useEffect(() => {
    if (nachErneut && status === 'geladen') {
      setNachErneut(false);
      statusRef.current?.focus();
    }
  }, [nachErneut, status]);

  useEffect(() => {
    if (fokusAnfordern > 0) {
      statusRef.current?.focus();
    }
  }, [fokusAnfordern]);

  // Nach "Weitere laden" steht der Fokus auf der ersten neuen Zeile -- auch wenn
  // sie in eine Gruppe weiter oben einsortiert wurde (WCAG 2.4.3).
  const [ersteNeue, setErsteNeue] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (ersteNeue !== undefined) {
      document.getElementById(zeilenId(ersteNeue))?.focus();
      setErsteNeue(undefined);
    }
  }, [ersteNeue]);

  const load = useCallback(
    async (fortsetzen?: string) => {
      const meinLauf = laufRef.current + 1;
      laufRef.current = meinLauf;
      const gueltig = (): boolean => laufRef.current === meinLauf;
      if (fortsetzen === undefined) setStatus('laedt');
      setLadefehler(undefined);
      try {
        const token = await accessToken();
        if (!gueltig()) return;
        if (token === undefined) {
          onReauthenticate();
          return;
        }
        const result = await fetchOwnTasks({ baseUrl: apiBaseUrl, accessToken: token, ...(fortsetzen === undefined ? {} : { cursor: fortsetzen }) });
        if (!gueltig()) return;
        if (result.outcome === 'rejected') {
          setLadefehler({ message: result.message, ...(fortsetzen === undefined ? {} : { cursor: fortsetzen }) });
          setStatus(fortsetzen === undefined ? 'abgelehnt' : 'geladen');
          return;
        }
        setEintraege((alt) => (fortsetzen === undefined ? result.tasks : [...alt, ...result.tasks]));
        if (fortsetzen !== undefined && result.tasks[0] !== undefined) setErsteNeue(result.tasks[0].taskId);
        setHasMore(result.hasMore);
        setCursor(result.nextCursor);
        setStatus('geladen');
      } catch {
        if (!gueltig()) return;
        setLadefehler({
          message: {
            headline: 'Deine Wiedervorlagen sind gerade nicht erreichbar.',
            explanation: 'Es kam keine Antwort.',
            nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
            retryable: true,
          },
          ...(fortsetzen === undefined ? {} : { cursor: fortsetzen }),
        });
        setStatus(fortsetzen === undefined ? 'abgelehnt' : 'geladen');
      }
    },
    [accessToken, apiBaseUrl, onReauthenticate],
  );

  useEffect(() => {
    void load();
    return () => {
      laufRef.current += 1;
    };
  }, [load]);

  const abhaken = async (eintrag: TaskView, art: 'erledigt' | 'zurueckgezogen'): Promise<void> => {
    setBusy(true);
    setMessage(undefined);
    setBestaetigung(undefined);
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const optionen = { baseUrl: apiBaseUrl, accessToken: token, taskId: eintrag.taskId, lockVersion: eintrag.lockVersion };
      const ergebnis = art === 'erledigt' ? await completeFollowUp(optionen) : await cancelFollowUp(optionen);
      if (ergebnis.outcome === 'applied') {
        // Die Zeile verschwindet, weil die Liste nur OFFENE zeigt. Deshalb
        // sagt die Statusregion, was geschehen ist -- sonst waere das Ergebnis
        // der Aktion nur ein Loch in der Liste (NFR-ACC-001, CM-UX-002).
        const satz =
          art === 'erledigt'
            ? `Erledigt: ${eintrag.title} bei ${eintrag.partyDisplayName}.`
            : `Zurückgezogen: ${eintrag.title} bei ${eintrag.partyDisplayName}. Brauchst du sie doch, setz an der Notiz eine neue.`;
        setBestaetigung(satz);
        if (art === 'erledigt') {
          setRueckgang({ satz, taskId: ergebnis.taskId, lockVersion: ergebnis.lockVersion, titel: eintrag.title, name: eintrag.partyDisplayName });
        }
        await load();
      } else if (ergebnis.outcome === 'gone') {
        setMessage({
          headline: 'Diese Wiedervorlage gibt es nicht mehr.',
          explanation: 'Sie wurde entfernt, oder du darfst den Kontakt nicht mehr sehen.',
          nextStep: 'Lade die Liste neu, um den aktuellen Stand zu sehen.',
          retryable: true,
        });
        await load();
      } else if (ergebnis.outcome === 'rejected') {
        setMessage(ergebnis.message);
        await load();
      }
    } catch {
      setMessage({
        headline: 'LIAISON ist gerade nicht erreichbar.',
        explanation: 'Deine Wiedervorlage wurde nicht geändert.',
        nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
        retryable: true,
      });
    } finally {
      setBusy(false);
      setFokusAnfordern((zahl) => zahl + 1);
    }
  };

  // Das Verschieben in der Liste (M-05 a): derselbe Befehl wie am Verlauf, mit dem Sperrstand der Zeile.
  const [verschiebenFuer, setVerschiebenFuer] = useState<string | undefined>(undefined);
  const [wahl, setWahl] = useState<FaelligkeitId>('morgen');
  const [andererTag, setAndererTag] = useState('');
  const [tagProblem, setTagProblem] = useState<string | undefined>(undefined);
  const [fokusZeile, setFokusZeile] = useState<{ readonly taskId: string; readonly ziel: 'zeile' | 'knopf' } | undefined>(undefined);
  useEffect(() => {
    if (verschiebenFuer !== undefined) document.getElementById(`verschieben-wahl-${verschiebenFuer}`)?.focus();
  }, [verschiebenFuer]);
  useEffect(() => {
    if (fokusZeile === undefined) return;
    const zeile = document.getElementById(zeilenId(fokusZeile.taskId));
    // Breit der Knopf "Verschieben", schmal das "…": zurueck geht es an den, der zu sehen ist.
    const knopf = [...(zeile?.querySelectorAll<HTMLButtonElement>('[data-verschieben]') ?? [])].find((k) => k.offsetParent !== null);
    // Die Zeile kann nach dem Neuladen auf einer spaeteren Seite liegen: dann die Statusregion, nie BODY.
    if (fokusZeile.ziel === 'knopf' && knopf !== null && knopf !== undefined) knopf.focus();
    else if (zeile !== null) zeile.focus();
    else statusRef.current?.focus();
    setFokusZeile(undefined);
  }, [fokusZeile, eintraege]);

  const verschiebenOeffnen = (eintrag: TaskView): void => {
    setVerschiebenFuer(eintrag.taskId);
    setWahl('morgen');
    setAndererTag('');
    setTagProblem(undefined);
  };
  const verschiebenAbbrechen = (taskId: string): void => {
    setVerschiebenFuer(undefined);
    setFokusZeile({ taskId, ziel: 'knopf' });
  };
  const verschieben = async (eintrag: TaskView): Promise<void> => {
    const heute = todayInBerlin();
    const tag = wahl === 'anderer' ? andererTag : faelligkeitFuer(wahl, heute);
    const problem = wahl === 'anderer' ? dueOnFieldProblem(andererTag, heute) : undefined;
    if (problem !== undefined || tag === undefined) {
      setTagProblem(problem ?? 'Wähle einen Tag.');
      document.getElementById(`verschieben-tag-${eintrag.taskId}`)?.focus();
      return;
    }
    setBusy(true);
    setMessage(undefined);
    setBestaetigung(undefined);
    setRueckgang(undefined);
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const ergebnis = await changeFollowUpDue({ baseUrl: apiBaseUrl, accessToken: token, taskId: eintrag.taskId, lockVersion: eintrag.lockVersion, dueOn: tag });
      if (ergebnis.outcome === 'applied') {
        setBestaetigung(`Verschoben: ${eintrag.title} bei ${eintrag.partyDisplayName}, ${dueLabel(ergebnis.dueOn, false)}.`);
        setVerschiebenFuer(undefined);
        await load();
        setFokusZeile({ taskId: eintrag.taskId, ziel: 'zeile' });
        return;
      }
      if (ergebnis.outcome === 'field-rejected') {
        setTagProblem(ergebnis.message);
        return;
      }
      setVerschiebenFuer(undefined);
      setMessage(
        ergebnis.outcome === 'rejected'
          ? ergebnis.message
          : {
              headline: 'Diese Wiedervorlage gibt es nicht mehr.',
              explanation: 'Sie wurde entfernt, oder du darfst den Kontakt nicht mehr sehen.',
              nextStep: 'Lade die Liste neu, um den aktuellen Stand zu sehen.',
              retryable: true,
            },
      );
      await load();
      setFokusAnfordern((zahl) => zahl + 1);
    } catch {
      setMessage({
        headline: 'LIAISON ist gerade nicht erreichbar.',
        explanation: 'Der Termin wurde nicht geändert.',
        nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
        retryable: true,
      });
      setFokusAnfordern((zahl) => zahl + 1);
    } finally {
      setBusy(false);
    }
  };

  const wiederOeffnen = async (ziel: NonNullable<typeof rueckgang>): Promise<void> => {
    setBusy(true);
    setMessage(undefined);
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const ergebnis = await reopenFollowUp({ baseUrl: apiBaseUrl, accessToken: token, taskId: ziel.taskId, lockVersion: ziel.lockVersion });
      // Der Knopf verschwindet in jedem Fall: nach Erfolg und nach Ablehnung ist der Stand ein anderer.
      setRueckgang(undefined);
      if (ergebnis.outcome === 'applied') {
        // Die Antwort traegt kein `overdue`; der Kalendertag bleibt, also entscheidet das Heute.
        setBestaetigung(`Wieder offen: ${ziel.titel} bei ${ziel.name}, ${dueLabel(ergebnis.dueOn, ergebnis.dueOn < todayInBerlin())}.`);
      } else {
        setBestaetigung(undefined);
        setMessage(
          ergebnis.outcome === 'rejected'
            ? ergebnis.message
            : ergebnis.outcome === 'field-rejected'
              ? { headline: 'Das hat nicht funktioniert.', explanation: ergebnis.message, nextStep: 'Die Liste zeigt jetzt den aktuellen Stand.', retryable: false }
              : {
                  headline: 'Diese Wiedervorlage gibt es nicht mehr.',
                  explanation: 'Sie wurde entfernt, oder du darfst den Kontakt nicht mehr sehen.',
                  nextStep: 'Lade die Liste neu, um den aktuellen Stand zu sehen.',
                  retryable: true,
                },
        );
      }
      await load();
    } catch {
      setMessage({
        headline: 'LIAISON ist gerade nicht erreichbar.',
        explanation: 'Deine Wiedervorlage wurde nicht geändert.',
        nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
        retryable: true,
      });
    } finally {
      setBusy(false);
      setFokusAnfordern((zahl) => zahl + 1);
    }
  };

  const gruppen = groupByDue(eintraege, new Date());
  const zeigtListe = status === 'geladen' && eintraege.length > 0;
  const regionZeigt =
    status === 'laedt' ||
    bestaetigung !== undefined ||
    message !== undefined ||
    ladefehler !== undefined ||
    (status === 'geladen' && eintraege.length === 0);

  return (
    <>
      <div className={own.kopf}>
        <Title3 as="h1" id={TITEL_ID} className={own.titel}>
          Wiedervorlagen
        </Title3>
        {zeigtListe ? (
          <Body1 className={own.zaehlsatz} aria-hidden="true">
            {openSummary(eintraege.length, hasMore)}
          </Body1>
        ) : undefined}
      </div>

      <Card className={own.karte}>
        <div aria-live="polite" role="status" aria-label="Status: Wiedervorlagen" id={STATUS_REGION_ID} ref={statusRef} tabIndex={-1} className={own.region}>
          {regionZeigt ? (
            <div className={own.regionInhalt}>
              {status === 'laedt' ? <Skelett ladesatz="Wiedervorlagen werden geladen" zeilen={SKELETT_WIEDERVORLAGEN} /> : undefined}
              {bestaetigung !== undefined ? (
                <MessageBar intent="success">
                  <MessageBarBody>
                    <MessageBarTitle>Gespeichert</MessageBarTitle>
                    <span>{bestaetigung}</span>
                  </MessageBarBody>
                  {rueckgang !== undefined && rueckgang.satz === bestaetigung ? (
                    <MessageBarActions>
                      <Button appearance="secondary" size="small" disabled={busy} onClick={() => void wiederOeffnen(rueckgang)}>
                        Rückgängig
                      </Button>
                    </MessageBarActions>
                  ) : undefined}
                </MessageBar>
              ) : undefined}
              {message !== undefined ? (
                <MessageBar intent="warning">
                  <MessageBarBody>
                    <MessageBarTitle>{message.headline}</MessageBarTitle>
                    <div>{message.explanation}</div>
                    <div>{message.nextStep}</div>
                  </MessageBarBody>
                </MessageBar>
              ) : undefined}
              {ladefehler !== undefined && ladefehler.cursor !== undefined ? (
                <MessageBar intent="warning">
                  <MessageBarBody>
                    <MessageBarTitle>{ladefehler.message.headline}</MessageBarTitle>
                    <div>{ladefehler.message.explanation}</div>
                    <div>{ladefehler.message.nextStep}</div>
                  </MessageBarBody>
                </MessageBar>
              ) : undefined}
              {status === 'abgelehnt' && ladefehler !== undefined && ladefehler.cursor === undefined ? (
                <Zustand
                  art="fehler"
                  titel={ladefehler.message.headline}
                  saetze={[ladefehler.message.explanation, ladefehler.message.nextStep]}
                  haupt={
                    ladefehler.message.retryable
                      ? {
                          label: 'Erneut versuchen',
                          onClick: () => {
                            setNachErneut(true);
                            void load();
                          },
                        }
                      : { label: 'Zu den Kontakten', href: '/kontakte' }
                  }
                  fokus={nachErneut}
                />
              ) : undefined}
              {status === 'geladen' && eintraege.length === 0 && ladefehler === undefined ? (
                <Zustand
                  art="leer"
                  titel="Alles erledigt"
                  saetze={[taskSummary(0, false), 'Was du dir an einer Notiz vormerkst, steht hier. Neue setzt du beim Kontakt, an der Notiz.']}
                  haupt={{ label: 'Zu den Kontakten', href: '/kontakte' }}
                />
              ) : undefined}
            </div>
          ) : undefined}
          {zeigtListe ? <Caption1 className={own.verborgen}>{taskSummary(eintraege.length, hasMore)}</Caption1> : undefined}
        </div>
        {/* Hinter der Statusregion, nicht in ihr: die Zeile hat ihre eigene Ansage. */}
        {status === 'abgelehnt' && ladefehler !== undefined && ladefehler.cursor === undefined && ladefehler.message.correlationId !== undefined ? (
          <Vorgang nummer={ladefehler.message.correlationId} eingebettet={false} />
        ) : undefined}

        {zeigtListe
          ? gruppen.map(({ gruppe, eintraege: inGruppe }, index) => {
              const kopfId = `wiedervorlagen-gruppe-${gruppe}`;
              return (
                // div statt section: die h2 und die benannte Liste tragen die Gruppe, eine Landmark je Gruppe waere Rauschen.
                <div key={gruppe}>
                  <h2 id={kopfId} className={`${own.gruppenkopf} ${gruppe === 'ueberfaellig' ? own.gruppenkopfUeberfaellig : ''}`.trim()}>
                    {FAELLIGKEITS_GRUPPEN[gruppe]}
                    {/* Kommen weitere Seiten, ist nur die letzte Gruppe unvollstaendig (Server liefert nach Faelligkeit): dort keine Zahl. */}
                    {hasMore && index === gruppen.length - 1 ? undefined : (
                      <>
                        {' '}
                        <span aria-hidden="true">·</span> {inGruppe.length}
                      </>
                    )}
                  </h2>
                  <ul className={own.liste} aria-labelledby={kopfId}>
                    {inGruppe.map((eintrag) => (
                      <li key={eintrag.taskId} id={zeilenId(eintrag.taskId)} tabIndex={-1} className={own.eintrag}>
                        <div className={own.text}>
                          <Body1Strong className={own.titelZeile}>{eintrag.title}</Body1Strong>
                          <Caption1 className={own.unterzeile}>
                            {dueLabel(eintrag.dueOn, eintrag.overdue)} · <AppLink to={partyHref(eintrag.partyId)}>{eintrag.partyDisplayName}</AppLink>
                          </Caption1>
                        </div>
                        <div className={own.knoepfe}>
                          <Button
                            appearance="secondary"
                            size="small"
                            className={own.knopf}
                            icon={<Haken />}
                            aria-label={taskButtonName('Erledigt', eintrag.title, eintrag.partyDisplayName)}
                            disabled={busy}
                            onClick={() => void abhaken(eintrag, 'erledigt')}
                          >
                            Erledigt
                          </Button>
                          {/* Fluents Knopf setzt display selbst; versteckt wird darum die Huelle (display: contents). */}
                          <span className={own.breit}>
                            <Button
                              appearance="subtle"
                              size="small"
                              className={own.knopf}
                              data-verschieben=""
                              aria-label={taskButtonName('Verschieben', eintrag.title, eintrag.partyDisplayName)}
                              aria-expanded={verschiebenFuer === eintrag.taskId}
                              disabled={busy}
                              onClick={() => (verschiebenFuer === eintrag.taskId ? verschiebenAbbrechen(eintrag.taskId) : verschiebenOeffnen(eintrag))}
                            >
                              Verschieben
                            </Button>
                            <Button
                              appearance="subtle"
                              size="small"
                              className={own.knopf}
                              aria-label={taskButtonName('Nicht mehr nötig', eintrag.title, eintrag.partyDisplayName)}
                              disabled={busy}
                              onClick={() => void abhaken(eintrag, 'zurueckgezogen')}
                            >
                              Nicht mehr nötig
                            </Button>
                          </span>
                          <span className={own.schmal}>
                            <Menu>
                              <MenuTrigger disableButtonEnhancement>
                                <MehrKnopf
                                  appearance="subtle"
                                  size="small"
                                  className={own.mehr}
                                  data-verschieben=""
                                  disabled={busy}
                                  bezug={`Weitere Aktionen: ${eintrag.title} bei ${eintrag.partyDisplayName}`}
                                  icon={<span aria-hidden="true">…</span>}
                                />
                              </MenuTrigger>
                              <MenuPopover>
                                <MenuList>
                                  <MenuItem aria-label={taskButtonName('Verschieben', eintrag.title, eintrag.partyDisplayName)} onClick={() => verschiebenOeffnen(eintrag)}>
                                    Verschieben
                                  </MenuItem>
                                  <MenuItem aria-label={taskButtonName('Nicht mehr nötig', eintrag.title, eintrag.partyDisplayName)} onClick={() => void abhaken(eintrag, 'zurueckgezogen')}>
                                    Nicht mehr nötig
                                  </MenuItem>
                                </MenuList>
                              </MenuPopover>
                            </Menu>
                          </span>
                        </div>
                        {verschiebenFuer === eintrag.taskId ? (
                          <form
                            className={own.verschieben}
                            aria-label={`Neuer Termin für ${eintrag.title} bei ${eintrag.partyDisplayName}`}
                            onSubmit={(event) => {
                              event.preventDefault();
                              void verschieben(eintrag);
                            }}
                            onKeyDown={(event) => {
                              if (event.key === 'Escape') verschiebenAbbrechen(eintrag.taskId);
                            }}
                          >
                            <Field label="Neuer Termin" className={own.verschiebenFeld}>
                              <Select
                                id={`verschieben-wahl-${eintrag.taskId}`}
                                value={wahl}
                                onChange={(_event, data) => {
                                  setWahl(data.value as FaelligkeitId);
                                  setTagProblem(undefined);
                                }}
                              >
                                {VERSCHIEBEN_OPTIONEN.map((option) => (
                                  <option key={option.id} value={option.id}>
                                    {option.label}
                                  </option>
                                ))}
                              </Select>
                            </Field>
                            {wahl === 'anderer' ? (
                              <Field label="Tag" className={own.verschiebenFeld} validationMessage={tagProblem ?? null} validationState={tagProblem === undefined ? 'none' : 'error'}>
                                <Input
                                  id={`verschieben-tag-${eintrag.taskId}`}
                                  type="date"
                                  value={andererTag}
                                  onChange={(_event, data) => {
                                    setAndererTag(data.value);
                                    setTagProblem(undefined);
                                  }}
                                />
                              </Field>
                            ) : tagProblem !== undefined ? (
                              <Caption1 role="alert">{tagProblem}</Caption1>
                            ) : undefined}
                            <Button appearance="secondary" size="small" className={own.knopf} type="submit" disabled={busy}>
                              Termin ändern
                            </Button>
                            <NebenKnopf size="small" className={own.knopf} type="button" disabled={busy} onClick={() => verschiebenAbbrechen(eintrag.taskId)}>
                              Abbrechen
                            </NebenKnopf>
                          </form>
                        ) : undefined}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })
          : undefined}
      </Card>

      {(status === 'geladen' && hasMore) || (ladefehler?.cursor !== undefined && ladefehler.message.retryable) ? (
        <div className={own.aktionen}>
          {status === 'geladen' && hasMore ? (
            <Button appearance="secondary" disabled={busy} onClick={() => void load(cursor)}>
              Weitere laden
            </Button>
          ) : undefined}
          {ladefehler?.cursor !== undefined && ladefehler.message.retryable ? (
            <Button appearance="secondary" disabled={busy} onClick={() => void load(ladefehler.cursor)}>
              Weitere erneut laden
            </Button>
          ) : undefined}
        </div>
      ) : undefined}
    </>
  );
}
